import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import type { ContentProps, PopupProps, TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { defaultErrorText, PopupErrorFallback } from './error-fallback';
import { FocusInsideOnLoad } from './focus-inside-on-load';
import { Loading } from './loading';
import { useClickToCloseRef } from './use-click-to-close-ref';

const styles = cssMap({
	// Contain the absolute live region without adding layout space or document overflow.
	statusContainer: {
		position: 'fixed',
		insetBlockStart: 0,
		insetInlineStart: 0,
		width: 0,
		height: 0,
		pointerEvents: 'none',
	},
});

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

const FallbackComponent = ({
	fallback,
	loadingText,
	onMount,
	onUnmount,
	shouldFocus,
}: {
	fallback?: ReactNode;
	loadingText?: ReactNode;
	onMount: () => void;
	onUnmount: () => void;
	shouldFocus: boolean;
}) => {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (shouldFocus) {
			ref.current?.focus();
		}

		onMount();
		return () => onUnmount();
	}, [onMount, onUnmount, shouldFocus]);

	return (
		<span ref={ref} tabIndex={shouldFocus ? -1 : undefined} data-testid="popup-trigger-loading">
			{fallback || <Loading loadingText={loadingText} />}
		</span>
	);
};

export type PopupTriggerProps<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointProps<TEntryPoint>, 'onClose' | 'onContentResized'> & {
		onClose?: () => void;
		onContentResized?: ContentProps['update'];
	};
	errorFallback?: ErrorFallback;
	/**
	 * The title that renders during the error screen.
	 * Only used when `platform_dst-a11y_accessible-error-popup` is enabled.
	 */
	errorTitle?: ReactNode;
	/**
	 * The text rendered during the error screen and announced in the live region.
	 * Only used when `platform_dst-a11y_accessible-error-popup` is enabled.
	 */
	errorText?: ReactNode;
	fallback?: ReactNode;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	forcedReportErrorUFO?: boolean;
	onError?: ErrorCallback;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	preloadOnFocus?: boolean;
	/**
	 * The text that renders in the default loading fallback and is read to users
	 * of assistive technology when loading fallback is mounted. Unused if the
	 * `fallback` prop is provided. Only used when flag
	 * `platform_dst-accessible-popup-loading` is on.
	 */
	loadingText?: ReactNode;
	/**
	 * The text that is read to users of assistive technology when loading is
	 * complete. This is only read if the loading fallback is mounted. Only used
	 * when flag `platform_dst-accessible-popup-loading` is on.
	 */
	loadingCompleteText?: ReactNode;
	trigger: (triggerProps: TriggerProps) => ReactNode;
} & CustomPopupProps;

export function PopupTrigger<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	errorText,
	errorTitle,
	onError,
	forcedReportErrorUFO,
	onClose,
	preloadOnFocus = true,
	autoFocus,
	loadingText = 'Loading popup...',
	loadingCompleteText = 'Loading is complete.',
	...popupProps
}: PopupTriggerProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);
	const [statusText, setStatusText] = useState<ReactNode>('');
	const isOpenRef = useRef(false);
	const hasErrorRef = useRef(false);
	const statusAnnounceTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const statusClearTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const updateRef = useRef<(() => void) | null>(null);
	const setInitialFocusRef = useRef<ContentProps['setInitialFocusRef'] | null>(null);

	const resetStatus = useCallback(() => {
		if (statusAnnounceTimeoutIdRef.current !== null) {
			clearTimeout(statusAnnounceTimeoutIdRef.current);
			statusAnnounceTimeoutIdRef.current = null;
		}
		if (statusClearTimeoutIdRef.current !== null) {
			clearTimeout(statusClearTimeoutIdRef.current);
			statusClearTimeoutIdRef.current = null;
		}
		setStatusText('');
	}, []);

	const clearStatusAfterDelay = useCallback(() => {
		statusClearTimeoutIdRef.current = setTimeout(() => {
			setStatusText('');
			statusClearTimeoutIdRef.current = null;
		}, 5000);
	}, []);

	const handleError = useCallback<ErrorCallback>(
		(props) => {
			if (fg('platform_dst-a11y_accessible-error-popup') && isOpenRef.current) {
				hasErrorRef.current = true;
				resetStatus();
				setStatusText(errorText ?? defaultErrorText);
				clearStatusAfterDelay();
			}
			onError?.(props);
		},
		[clearStatusAfterDelay, errorText, onError, resetStatus],
	);

	useEffect(() => {
		return () => {
			if (statusAnnounceTimeoutIdRef.current !== null) {
				clearTimeout(statusAnnounceTimeoutIdRef.current);
			}
			if (statusClearTimeoutIdRef.current !== null) {
				clearTimeout(statusClearTimeoutIdRef.current);
			}
		};
	}, []);

	const handleOpen = useCallback(() => {
		isOpenRef.current = true;
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			isOpenRef.current = false;
			setIsOpen(false);
			if (
				fg('platform_dst-accessible-popup-loading') ||
				fg('platform_dst-a11y_accessible-error-popup')
			) {
				resetStatus();
			}
			onClose?.(event, currentLevel);
		},
		[onClose, resetStatus],
	);

	const triggerCloseRef = useClickToCloseRef({
		isOpen,
		handleClose,
	});

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const popupSetInitialFocusRef = useRef<((el: HTMLElement | null) => void) | null>(null);
	const handleSetInitialFocusRef = useCallback((el: HTMLElement | null) => {
		popupSetInitialFocusRef.current?.(el);
	}, []);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			hasErrorRef.current = false;
			handleOpen();

			setContent(
				<>
					<InternalEntryPointContainer
						id={id}
						entryPointReference={entryPointReference}
						fallback={
							// when the fallback is unmounted we want to trigger an update to the popup position
							<FallbackComponent
								fallback={fallback}
								loadingText={loadingText}
								onMount={() => {
									if (fg('platform_dst-accessible-popup-loading')) {
										if (statusClearTimeoutIdRef.current !== null) {
											clearTimeout(statusClearTimeoutIdRef.current);
											statusClearTimeoutIdRef.current = null;
										}
										statusAnnounceTimeoutIdRef.current = setTimeout(() => {
											// Delaying by a little bit to ensure that the live region
											// populates correctly. Doing it immediately can cause
											// assistive technology to ignore it.
											setStatusText(loadingText);
											statusAnnounceTimeoutIdRef.current = null;
										}, 100);
									}
								}}
								onUnmount={() => {
									if (fg('platform_dst-accessible-popup-loading') && !hasErrorRef.current) {
										if (statusAnnounceTimeoutIdRef.current !== null) {
											// If the initial status update from `onMount` has not run
											// yet, cancel it because the user does not need to be informed
											clearTimeout(statusAnnounceTimeoutIdRef.current);
											statusAnnounceTimeoutIdRef.current = null;
										} else if (isOpenRef.current) {
											setStatusText(loadingCompleteText);
											// Ensure it is cleared to prevent any screenreader on browser mode from arrowing into the element.
											clearStatusAfterDelay();
										}
									}

									return updateRef.current?.();
								}}
								shouldFocus={fg('platform_dst-a11y_popup-focus-on-load') && autoFocus !== false}
							/>
						}
						errorFallback={
							errorFallback ??
							(fg('platform_dst-a11y_accessible-error-popup')
								? ({ error }: { error: Error }) => (
										<PopupErrorFallback
											error={error}
											errorText={errorText}
											errorTitle={errorTitle}
										/>
									)
								: undefined)
						}
						runtimeProps={
							{
								...(entryPointProps ?? emptyEntryPointProps),
								onContentResized: () => {
									updateRef.current?.();
								},
								onClose: handleClose,
								...(fg('platform_dst-popup-trigger-initial-focus-ref')
									? { setInitialFocusRef: handleSetInitialFocusRef }
									: {}),
							} as unknown as EntryPointProps<TEntryPoint>
						}
						onError={handleError}
						forcedError={forcedReportErrorUFO}
					/>
					{fg('platform_dst-a11y_popup-focus-on-load') &&
						// Omit means `Popup`'s own default (`true`). Only an explicit
						// `false` opts out of post-load focus.
						autoFocus !== false && (
							<FocusInsideOnLoad
								entryPointReference={entryPointReference}
								setInitialFocusRef={setInitialFocusRef}
							/>
						)}
				</>,
			);
		},
		[
			setContent,
			id,
			isOpen,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			errorText,
			errorTitle,
			handleOpen,
			handleError,
			clearStatusAfterDelay,
			handleClose,
			handleSetInitialFocusRef,
			autoFocus,
			loadingCompleteText,
			loadingText,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([triggerRef, triggerProps.ref, triggerCloseRef]);
			return trigger({ ...triggerProps, ref: mergedRef });
		},
		[trigger, triggerRef, triggerCloseRef],
	);

	const popupContent = useCallback(
		({ update, setInitialFocusRef }: ContentProps) => {
			updateRef.current = update;
			if (fg('platform_dst-popup-trigger-initial-focus-ref')) {
				popupSetInitialFocusRef.current = setInitialFocusRef;
			}
			return content;
		},
		[content],
	);

	return (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent -- This would be complex to rollout and top layer is rolling this out soon */}
			<Popup
				// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
				{...popupProps}
				autoFocus={autoFocus}
				isOpen={isOpen}
				onClose={handleClose}
				trigger={popupTrigger}
				content={popupContent}
			/>
			{fg('platform_dst-accessible-popup-loading') ||
			fg('platform_dst-a11y_accessible-error-popup') ? (
				// This is required because focus will not necessarily move into the
				// popup on mount during load, and there is not necessarily a focus
				// trap. Because of this, we need to have a live region to ensure
				// messaging comes across to the user. It also must be added on page
				// load for it to be read properly.
				<Box xcss={styles.statusContainer}>
					<VisuallyHidden role="status">{statusText}</VisuallyHidden>
				</Box>
			) : null}
		</>
	);
}
